import { humaniseParticipantKey, normaliseParticipantKey } from './format'

export const childTicketValidationMessage = 'Children must be accompanied by an adult or senior.'
export const duplicateSessionCartMessage = 'This session is already in your cart. Edit the existing cart item to change its ticket quantities.'

export const attractionAllowsChildrenOnly = (attraction = {}) => {
  if (typeof attraction?.allow_children_only === 'boolean') {
    return attraction.allow_children_only
  }

  return ['1', 'true', 'yes', 'on'].includes(String(attraction?.allow_children_only ?? '').trim().toLowerCase())
}

export const resolveTicketOptions = (item = {}) => {
  if (Array.isArray(item?.availability?.priceOptions) && item.availability.priceOptions.length > 0) {
    return item.availability.priceOptions
  }

  if (Array.isArray(item?.prices) && item.prices.length > 0) {
    return item.prices
  }

  return []
}

export const getParticipantCount = (participants = {}, key = '') => {
  if (!participants || !key) {
    return 0
  }

  return Number(participants[key] ?? participants[humaniseParticipantKey(key)] ?? 0)
}

export const setParticipantCount = (participants = {}, key = '', value = 0) => {
  const next = { ...participants }
  next[key] = value
  return next
}

export const buildParticipantSummary = (participants = {}) => {
  return Object.entries(participants)
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${count} ${humaniseParticipantKey(normaliseParticipantKey(key))}`)
}

export const getTicketPrice = (ticket = {}) => Number(ticket?.price ?? ticket?.retailPrice ?? 0)

export const getExtraRows = (item = {}) => (item.extras ?? []).map((extra, index) => ({
  key: `extra-${extra.id ?? index}`,
  label: extra.displayName ?? extra.name ?? 'Extra',
  count: Number(extra.quantity ?? 0),
  price: Number(extra.price ?? 0),
  total: Number(extra.price ?? 0) * Number(extra.quantity ?? 0),
})).filter((extra) => extra.count > 0)

export const getItemTotal = (item = {}) => {
  return resolveTicketOptions(item).reduce((total, ticket) => {
    const key = normaliseParticipantKey(ticket?.label ?? ticket?.name ?? '')
    const count = getParticipantCount(item?.participants, key)
    return total + (count * getTicketPrice(ticket))
  }, 0) + getExtraRows(item).reduce((total, extra) => total + extra.total, 0)
}

export const getSeatsUsed = (item = {}, participants = item?.participants ?? {}) => {
  return resolveTicketOptions(item).reduce((total, ticket) => {
    const key = normaliseParticipantKey(ticket?.label ?? ticket?.name ?? '')
    const count = getParticipantCount(participants, key)
    const seatsUsed = Number(ticket?.seatsUsed ?? 1)
    return total + (count * seatsUsed)
  }, 0)
}

export const findTicketOption = (item = {}, key = '') => {
  return resolveTicketOptions(item).find((ticket) => normaliseParticipantKey(ticket?.label ?? ticket?.name ?? '') === key)
}

export const childTicketNeedsAdultOrSenior = (item = {}, attraction = {}) => {
  if (attractionAllowsChildrenOnly(attraction)) {
    return false
  }

  const children = getParticipantCount(item?.participants, 'children')

  if (children <= 0) {
    return false
  }

  const adults = getParticipantCount(item?.participants, 'adults')
  const seniors = getParticipantCount(item?.participants, 'seniors')

  return adults + seniors < 1
}
